import {
  AfterViewInit,
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  ElementRef,
  EventEmitter,
  forwardRef,
  HostListener,
  Input,
  OnDestroy,
  OnInit,
  Output,
  ViewChild,
} from '@angular/core';
import {
  ControlValueAccessor,
  FormControl,
  NG_VALUE_ACCESSOR,
} from '@angular/forms';
import { Subject } from 'rxjs';
import { debounceTime, distinctUntilChanged, takeUntil } from 'rxjs/operators';
import { COLOR_PALETTE, FORMAT_STYLES } from './constants';
import { EditorCommandType, RichTextEditorCommandService } from './rich-text-editor-command.service';
import { IToolbarAction } from './toolbar/toolbar.component';

export interface IRichTextEditorSettings {
  minHeight?: number;
  maxHeight?: number | null;
  disabled?: boolean;
  required?: boolean;
  readonly?: boolean;
  autofocus?: boolean;
}

export interface IFormatHandler {
  [key: string]: () => void
}

@Component({
  selector: 'sgt-rich-text-editor',
  templateUrl: './rich-text-editor.component.html',
  styleUrls: ['./rich-text-editor.component.scss'],
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: forwardRef(() => RichTextEditorComponent),
      multi: true,
    },
  ],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class RichTextEditorComponent
  implements OnInit, AfterViewInit, OnDestroy, ControlValueAccessor {

  @Input() placeholder = 'Digite o texto...';
  @Input() id = '';
  @Input() control?: FormControl;
  @Input() settings: IRichTextEditorSettings = {
    minHeight: 200,
    maxHeight: null,
    disabled: false,
    required: false,
    readonly: false,
    autofocus: false,
  };

  @Output() contentChange = new EventEmitter<string>();
  @Output() blur = new EventEmitter<void>();
  @Output() focus = new EventEmitter<void>();

  content = '';
  isFocused = false;

  showTextColorPicker = false;
  showBgColorPicker = false;
  customTextColor = '#000';
  customBgColor = '#fff';

  formatStyles = FORMAT_STYLES;
  colorPalette = COLOR_PALETTE;
  currentFormat = '';

  private destroy$ = new Subject<void>();
  private contentChange$ = new Subject<string>();
  private onChange: (value: string) => void = () => {};
  private onTouched: () => void = () => {};

  @ViewChild('editor', { static: true }) editorRef: ElementRef<HTMLDivElement>;

  constructor(
    private cdr: ChangeDetectorRef,
    private commandService: RichTextEditorCommandService
  ) {}

  ngOnInit(): void {
    this.setupContentChangeObservable();
  }

  ngAfterViewInit(): void {
    this.initEditor();

    if (this.settings.autofocus) {
      this.setFocus();
    }
  }

  writeValue(value: string): void {
    this.content = value || '';
    if (this.editorRef?.nativeElement) {
      this.editorRef.nativeElement.innerHTML = this.content;
    }
    this.cdr.markForCheck();
  }

  registerOnChange(fn: any): void {
    this.onChange = fn;
  }

  registerOnTouched(fn: any): void {
    this.onTouched = fn;
  }

  setDisabledState(isDisabled: boolean): void {
    this.settings.disabled = isDisabled;
    this.updateEditableState();
    this.cdr.markForCheck();
  }

  private initEditor(): void {
    this.editorRef.nativeElement.innerHTML = this.content;
    this.updateEditableState();
    this.applyStyles();
  }

  private setupContentChangeObservable(): void {
    this.contentChange$
      .pipe(
        debounceTime(300),
        distinctUntilChanged(),
        takeUntil(this.destroy$)
      ).subscribe((content) => {
        this.onChange(content);
        this.contentChange.emit(content);
        this.validateControl();
      });
  }

  onInput(): void {
    const htmlContent = this.editorRef.nativeElement.innerHTML;
    const textContent = this.editorRef.nativeElement.textContent?.trim() || '';

    this.content = textContent ? htmlContent : '';
    this.contentChange$.next(this.content);
    this.updateButtonStates();
    this.cdr.markForCheck();
  }

  onFocus(): void {
    this.isFocused = true;
    this.focus.emit();
    this.updateButtonStates();
    this.cdr.markForCheck();
  }

  onBlur(): void {
    this.isFocused = false;
    this.onTouched();
    this.blur.emit();
    this.cdr.markForCheck();
  }

  onMouseUp(): void {
    this.updateButtonStates();
  }

  onKeyUp(): void {
    this.updateButtonStates();
  }

  onKeyDown(event: KeyboardEvent): void {
    if (event.key !== 'Enter') {
return;
}

    const selection = window.getSelection();
    if (!selection?.rangeCount) {
return;
}

    const element = this.getCurrentElement(selection);
    const blockquote = element?.closest('blockquote');

    if (!blockquote) {
return;
}

    const isCurrentLineEmpty = !element.textContent?.trim();
    const isBlockquoteEmpty = !blockquote.textContent.trim();

    if (isBlockquoteEmpty) {
      event.preventDefault();
      this.formatParagraph();
    } else if (isCurrentLineEmpty) {
      event.preventDefault();
      this.createParagraph(blockquote, selection);
    }
  }

  private getCurrentElement(selection: Selection): HTMLElement | null {
    const element = selection.getRangeAt(0).startContainer as HTMLElement;
    return element.nodeType === Node.TEXT_NODE ? element.parentElement : element;
  }

  private createParagraph(blockquote: Element, selection: Selection): void {
    const newParagraph = document.createElement('p');
    newParagraph.innerHTML = '<br>';

    blockquote.parentNode.insertBefore(
      newParagraph,
      blockquote.nextSibling || null
    );

    const range = document.createRange();
    range.setStart(newParagraph, 0);
    range.collapse(true);
    selection.removeAllRanges();
    selection.addRange(range);

    this.onInput();
  }

  execCommand(command: string, value: string | null = null): void {
    if (this.settings.disabled || this.settings.readonly) {
return;
}

    this.commandService.executeCommand(command as EditorCommandType, value);
    this.setFocus();
    this.onInput();
    this.updateButtonStates();
  }

  isCommandActive(command: string): boolean {
    return this.commandService.isCommandActive(command as EditorCommandType);
  }

  isAlignmentActive(alignment: string): boolean {
    return this.commandService.isCommandActive(alignment as EditorCommandType);
  }

  private updateButtonStates(): void {
    this.updateCurrentFormat();
    this.cdr.markForCheck();
  }

  private updateCurrentFormat(): void {
    this.currentFormat = this.commandService.getCurrentFormat(this.editorRef.nativeElement);
  }

  formatBold(): void {
    this.execCommand('bold');
  }

  formatUnderline(): void {
    this.execCommand('underline');
  }

  formatStrikethrough(): void {
    this.execCommand('strikethrough');
  }

  formatUnorderedList(): void {
    this.execCommand('insertUnorderedList');
  }

  formatOrderedList(): void {
    this.execCommand('insertOrderedList');
  }

  formatAlignLeft(): void {
    this.execCommand('justifyLeft');
  }

  formatAlignCenter(): void {
    this.execCommand('justifyCenter');
  }

  formatAlignRight(): void {
    this.execCommand('justifyRight');
  }

  formatAlignJustify(): void {
    this.execCommand('justifyFull');
  }

  formatHeading(level: number): void {
    if (level >= 1 && level <= 6) {
      this.execCommand('formatBlock', `<h${level}>`);
    }
  }

  formatParagraph(): void {
    this.execCommand('formatBlock', '<p>');
  }

  formatBlockquote(): void {
    this.execCommand('formatBlock', 'blockquote');
  }

  toggleTextColorPicker(): void {
    this.showTextColorPicker = !this.showTextColorPicker;
    this.showBgColorPicker = false;
    this.cdr.markForCheck();
  }

  toggleBgColorPicker(): void {
    this.showBgColorPicker = !this.showBgColorPicker;
    this.showTextColorPicker = false;
    this.cdr.markForCheck();
  }

  applyTextColor(color: string): void {
    this.execCommand('foreColor', color);
    this.showTextColorPicker = false;
    this.cdr.markForCheck();
  }

  applyBackgroundColor(color: string): void {
    this.execCommand('backColor', color);
    this.showBgColorPicker = false;
    this.cdr.markForCheck();
  }

  @HostListener('document:click', ['$event'])
  onDocumentClick(event: MouseEvent): void {
    const target = event.target as HTMLElement;
    const clickedInside = target.closest('.toolbar__color-picker');

    if (!clickedInside) {
      this.showTextColorPicker = false;
      this.showBgColorPicker = false;
      this.cdr.markForCheck();
    }
  }

  private formatHandlers: IFormatHandler = {
    p: () => this.formatParagraph(),
    h1: () => this.formatHeading(1),
    h2: () => this.formatHeading(2),
    h3: () => this.formatHeading(3),
    h4: () => this.formatHeading(4),
    h5: () => this.formatHeading(5),
    h6: () => this.formatHeading(6),
    blockquote: () => this.formatBlockquote(),
  };

  private actionHandlers: Record<string, (payload?: any) => void> = {
    bold: () => this.formatBold(),
    underline: () => this.formatUnderline(),
    strikethrough: () => this.formatStrikethrough(),
    unorderedList: () => this.formatUnorderedList(),
    orderedList: () => this.formatOrderedList(),
    alignLeft: () => this.formatAlignLeft(),
    alignCenter: () => this.formatAlignCenter(),
    alignRight: () => this.formatAlignRight(),
    alignJustify: () => this.formatAlignJustify(),
    formatChange: (payload) => {
      const format = payload?.format;
      if (format && this.formatHandlers[format]) {
        this.formatHandlers[format]();
        setTimeout(() => {
          this.updateCurrentFormat();
          this.cdr.markForCheck();
        }, 0);
      }
    },
    textColor: (payload) => this.applyTextColor(payload?.color),
    backgroundColor: (payload) => this.applyBackgroundColor(payload?.color),
    customTextColor: (payload) => {
      this.customTextColor = payload;
      this.applyTextColor(payload);
    },
    customBgColor: (payload) => {
      this.customBgColor = payload;
      this.applyBackgroundColor(payload);
    },
    toggleTextColorPicker: () => this.toggleTextColorPicker(),
    toggleBgColorPicker: () => this.toggleBgColorPicker(),
    insertLink: () => this.insertLink(),
    removeFormat: () => this.removeFormat(),
    undo: () => this.undo(),
    redo: () => this.redo(),
  };

  onToolbarAction(action: IToolbarAction): void {
    this.actionHandlers[action.type]?.(action.data);
  }

  insertLink(): void {
    const url = prompt('Digite a URL:');
    if (url) {
      this.execCommand('createLink', url);
    }
  }

  removeFormat(): void {
    this.execCommand('removeFormat');
  }

  undo(): void {
    this.execCommand('undo');
  }

  redo(): void {
    this.execCommand('redo');
  }

  setFocus(): void {
    if (!this.settings.disabled && !this.settings.readonly) {
      this.editorRef.nativeElement.focus();
    }
  }

  clear(): void {
    this.content = '';
    this.editorRef.nativeElement.innerHTML = '';
    this.onChange('');
    this.contentChange.emit('');
    this.cdr.markForCheck();
  }

  isEmpty(): boolean {
    const text = this.editorRef?.nativeElement?.textContent?.trim() || '';
    return text.length === 0;
  }

  getContent(): string {
    return this.content;
  }

  getHtmlContent(): string {
    return this.editorRef?.nativeElement?.innerHTML || '';
  }

  getTextContent(): string {
    return this.editorRef?.nativeElement?.textContent?.trim() || '';
  }

  private updateEditableState(): void {
    if (this.editorRef?.nativeElement) {
      this.editorRef.nativeElement.contentEditable = (
        !this.settings.disabled && !this.settings.readonly
      ).toString();
    }
  }

  private applyStyles(): void {
    if (this.editorRef?.nativeElement) {
      const style = this.editorRef.nativeElement.style;
      style.minHeight = `${this.settings.minHeight}px`;

      if (this.settings.maxHeight) {
        style.maxHeight = `${this.settings.maxHeight}px`;
      }
    }
  }

  private validateControl(): void {
    if (!this.control) {
return;
}

    const editorElement = this.editorRef.nativeElement;

    if (this.control.valid) {
      editorElement.classList.remove('ng-invalid');
      editorElement.classList.add('ng-valid');
    } else {
      editorElement.classList.remove('ng-valid');
      editorElement.classList.add('ng-invalid');
    }
  }

  get isInvalid(): boolean {
    return (
      this.control?.invalid &&
      (this.control?.dirty || this.control?.touched)
    );
  }

  get showPlaceholder(): boolean {
    return this.isEmpty() && !this.isFocused;
  }

  preventBlur(event: MouseEvent): void {
    const target = event.target as HTMLElement;

    if (target.tagName === 'SELECT' || target.tagName === 'INPUT') {
      return;
    }

    event.preventDefault();
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }
}
